import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Star, Users, Filter, ChevronDown, ChevronUp, ArrowUpDown, Search } from 'lucide-react';
import { getCateringServices } from '../../services/cateringApi';
import * as api from '../../services/api';
import SkeletonImage from '../skeletons/SkeletonImage';
import PriceRangeSlider from '../common/PriceRangeSlider';
import namkeFallback from '../../assets/namke-fallback.png';
import { getCateringServiceTypeLabel, getOfferStartingPrice } from '../../constants/catering';

const PRIMARY = '#054625';
const TERRA = '#EF4C23';

const inputStyle = {
  border: '1px solid rgba(5,16,4,0.14)',
  borderRadius: '999px',
  padding: '13px 20px',
  fontFamily: 'Poppins, sans-serif',
  fontSize: '15px',
  background: '#FCFBF5',
  color: '#051004',
  outline: 'none',
};

const filterLabelStyle = {
  fontFamily: 'Archivo, sans-serif',
  fontSize: '12.5px',
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'rgba(5,16,4,0.6)',
};

const EVENT_TYPE_KEYS = {
  mariage: 'wedding',
  wedding: 'wedding',
  cocktail: 'cocktail',
  anniversaire: 'birthday',
  birthday: 'birthday',
  seminaire: 'seminar',
  seminar: 'seminar',
  corporate: 'corporate',
  'corporate event': 'corporate',
  'menu dégustation': 'tastingMenu',
  'tasting menu': 'tastingMenu',
  barbecue: 'barbecue',
  bbq: 'barbecue',
  brunch: 'brunch',
  'plateau repas': 'mealTray',
  buffet: 'buffet',
};

const CateringListing = ({ formulaId } = {}) => {
  const { t } = useTranslation();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    city: '',
    cuisineType: [],
    capacity: [],
    priceRange: { min: 0, max: 200 },
    eventType: [],
    rating: []
  });
  const [availableCities, setAvailableCities] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    cuisineType: [],
    capacity: [],
    price: [],
    eventType: [],
    rating: []
  });
  const [expandedSections, setExpandedSections] = useState({
    cuisineType: false,
    capacity: false,
    price: false,
    eventType: false,
    rating: false
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('popularity');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

  useEffect(() => {
    fetchCateringRestaurants();
    fetchAvailableCities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formulaId]);

  const fetchAvailableCities = async () => {
    try {
      const response = await api.getRestaurants();
      if (response && Array.isArray(response)) {
        const cities = [...new Set(response.map(r => r.city).filter(Boolean))].sort();
        setAvailableCities(cities);
      } else if (response && response.restaurants) {
        const cities = [...new Set(response.restaurants.map(r => r.city).filter(Boolean))].sort();
        setAvailableCities(cities);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des villes:', error);
    }
  };

  const fetchCateringRestaurants = async () => {
    try {
      setLoading(true);

      const allServices = await getCateringServices(formulaId ? { formula_id: formulaId } : undefined);

      if (!allServices || allServices.length === 0) {
        setRestaurants([]);
        return;
      }

      const servicesGroupedByRestaurant = allServices.reduce((acc, service) => {
        if (!acc[service.restaurant_id]) {
          acc[service.restaurant_id] = [];
        }
        acc[service.restaurant_id].push(service);
        return acc;
      }, {});

      const restaurantIds = Object.keys(servicesGroupedByRestaurant);
      const restaurantsWithCatering = await Promise.all(
        restaurantIds.map(async (restaurantId) => {
          try {
            const restaurant = await api.getRestaurantById(restaurantId);

            let profile = null;
            try {
              const details = await api.getRestaurantDetails(restaurantId);
              profile = details.profile || details;
            } catch (error) {
              console.warn(`Profil non trouvé pour le restaurant ${restaurantId}`);
            }

            const cateringServices = servicesGroupedByRestaurant[restaurantId];

            const minCapacity = Math.min(...cateringServices.map(s => s.min_guests || 10));
            const maxCapacity = Math.max(...cateringServices.map(s => s.max_guests || 100));
            // « À partir de » : le plus petit prix par personne parmi les offres (0 si toutes au forfait)
            const perPersonPrices = cateringServices.map(getOfferStartingPrice).filter((p) => p.perPerson).map((p) => p.amount);
            const startingPricePerPerson = perPersonPrices.length ? Math.min(...perPersonPrices) : 0;

            const eventTypes = [...new Set(cateringServices.map(s => getCateringServiceTypeLabel(s.service_type)))];

            return {
              ...restaurant,
              profile,
              cateringServices: {
                services: cateringServices,
                eventTypes: eventTypes,
                minCapacity: minCapacity,
                maxCapacity: maxCapacity,
                pricePerPerson: startingPricePerPerson,
                includesService: cateringServices.some(s => s.staff_available),
                setupTime: cateringServices[0]?.preparation_time_hours || 24,
                specialties: [restaurant.cuisine_type || t('catering.listing.traditionalCuisine')]
              }
            };
          } catch (error) {
            console.error(`Erreur lors de la récupération du restaurant ${restaurantId}:`, error);
            return null;
          }
        })
      );

      const validRestaurants = restaurantsWithCatering.filter(restaurant => restaurant !== null);

      setRestaurants(validRestaurants);

      const cities = [...new Set(validRestaurants.map(r => r.city).filter(Boolean))].sort();
      if (cities.length > 0 && availableCities.length === 0) {
        setAvailableCities(cities);
      }

      generateFilterOptions(validRestaurants);
    } catch (err) {
      setError(t('catering.listing.loadError'));
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterType, value) => {
    if (filterType === 'priceRange') {
      setFilters(prev => ({
        ...prev,
        priceRange: value
      }));
      return;
    }
    setFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType].includes(value)
        ? prev[filterType].filter(item => item !== value)
        : [...prev[filterType], value]
    }));
  };

  const handleCityChange = (city) => {
    setFilters(prev => ({
      ...prev,
      city: city === '' ? '' : city
    }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      city: '',
      cuisineType: [],
      capacity: [],
      price: [],
      eventType: [],
      rating: []
    });
  };

  const generateFilterOptions = (restaurantsData) => {
    if (!restaurantsData || restaurantsData.length === 0) return;

    const cuisineTypes = [...new Set(
      restaurantsData
        .map(r => r.cuisine_type)
        .filter(Boolean)
        .map(c => c.toLowerCase())
    )].sort();
    const cuisineOptions = cuisineTypes.map(cuisine => ({
      value: cuisine,
      label: cuisine.charAt(0).toUpperCase() + cuisine.slice(1)
    }));

    const allMinCapacities = restaurantsData.map(r => r.cateringServices?.minCapacity || 0);
    const allMaxCapacities = restaurantsData.map(r => r.cateringServices?.maxCapacity || 0);
    const minCapacity = Math.min(...allMinCapacities);
    const maxCapacity = Math.max(...allMaxCapacities);

    const capacityRanges = [];
    let currentMin = Math.floor(minCapacity / 10) * 10;
    const step = 50;

    while (currentMin < maxCapacity) {
      const currentMax = currentMin + step;
      if (currentMax >= maxCapacity) {
        capacityRanges.push({
          value: `${currentMin}+`,
          label: t('catering.listing.capacityPlus', { min: currentMin })
        });
        break;
      } else {
        capacityRanges.push({
          value: `${currentMin}-${currentMax}`,
          label: t('catering.listing.capacityRange', { min: currentMin, max: currentMax })
        });
        currentMin = currentMax;
      }
    }

    const allPrices = restaurantsData
      .map(r => r.cateringServices?.pricePerPerson || 0)
      .filter(p => p > 0);

    let priceMin = 0;
    let priceMax = 200;

    if (allPrices.length > 0) {
      priceMin = Math.floor(Math.min(...allPrices) / 10) * 10;
      priceMax = Math.ceil(Math.max(...allPrices) / 10) * 10;
      if (priceMax < priceMin + 50) {
        priceMax = priceMin + 50;
      }
    }

    setFilters(prev => ({
      ...prev,
      priceRange: { min: priceMin, max: priceMax }
    }));

    const allEventTypes = new Set();
    restaurantsData.forEach(r => {
      if (r.cateringServices?.eventTypes) {
        r.cateringServices.eventTypes.forEach(event => {
          allEventTypes.add(event.toLowerCase());
        });
      }
    });

    const eventOptions = Array.from(allEventTypes)
      .map(event => {
        const normalized = event.toLowerCase();
        const i18nKey = EVENT_TYPE_KEYS[normalized];
        return {
          value: normalized,
          label: i18nKey ? t(`catering.listing.eventTypes.${i18nKey}`) : normalized.charAt(0).toUpperCase() + normalized.slice(1)
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    const allRatings = restaurantsData
      .map(r => r.rating || 0)
      .filter(r => r > 0);

    const minRating = allRatings.length > 0 ? Math.min(...allRatings) : 3.5;
    const maxRating = allRatings.length > 0 ? Math.max(...allRatings) : 5.0;

    const ratingOptions = [];
    if (maxRating >= 4.5) ratingOptions.push({ value: '4.5', label: t('catering.listing.ratingPlus', { rating: '4.5' }) });
    if (maxRating >= 4.0) ratingOptions.push({ value: '4.0', label: t('catering.listing.ratingPlus', { rating: '4.0' }) });
    if (maxRating >= 3.5 || minRating < 4.0) ratingOptions.push({ value: '3.5', label: t('catering.listing.ratingPlus', { rating: '3.5' }) });
    if (minRating < 3.5) ratingOptions.push({ value: '3.0', label: t('catering.listing.ratingPlus', { rating: '3.0' }) });

    setFilterOptions({
      cuisineType: cuisineOptions,
      capacity: capacityRanges.length > 0 ? capacityRanges : [
        { value: '10-50', label: t('catering.listing.capacityRange', { min: 10, max: 50 }) },
        { value: '50-100', label: t('catering.listing.capacityRange', { min: 50, max: 100 }) },
        { value: '100-200', label: t('catering.listing.capacityRange', { min: 100, max: 200 }) },
        { value: '200+', label: t('catering.listing.capacityPlus', { min: 200 }) }
      ],
      priceMin: priceMin,
      priceMax: priceMax,
      eventType: eventOptions.length > 0 ? eventOptions : [
        { value: 'mariage', label: t('catering.listing.eventTypes.wedding') },
        { value: 'cocktail', label: t('catering.listing.eventTypes.cocktail') },
        { value: 'buffet', label: t('catering.listing.eventTypes.buffet') }
      ],
      rating: ratingOptions.length > 0 ? ratingOptions : [
        { value: '4.5', label: t('catering.listing.ratingPlus', { rating: '4.5' }) },
        { value: '4.0', label: t('catering.listing.ratingPlus', { rating: '4.0' }) },
        { value: '3.5', label: t('catering.listing.ratingPlus', { rating: '3.5' }) }
      ]
    });
  };

  const sortOptions = [
    { value: 'popularity', label: t('catering.listing.sort.popularity') },
    { value: 'price-low', label: t('catering.listing.sort.priceLow') },
    { value: 'price-high', label: t('catering.listing.sort.priceHigh') },
    { value: 'rating', label: t('catering.listing.sort.rating') },
    { value: 'capacity', label: t('catering.listing.sort.capacity') }
  ];

  const filteredRestaurants = restaurants.filter(restaurant => {
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const nameMatch = (restaurant.name || '').toLowerCase().includes(q);
      const cityMatch = (restaurant.city || '').toLowerCase().includes(q);
      const eventMatch = (restaurant.cateringServices?.eventTypes || []).some(e => e.toLowerCase().includes(q));
      const specialtyMatch = (restaurant.cateringServices?.specialties || []).some(s => s.toLowerCase().includes(q));
      if (!nameMatch && !cityMatch && !eventMatch && !specialtyMatch) return false;
    }

    if (filters.city && filters.city !== '' && restaurant.city && !restaurant.city.toLowerCase().includes(filters.city.toLowerCase())) return false;

    if (filters.cuisineType.length > 0 && !filters.cuisineType.some(cuisine =>
      restaurant.cateringServices.specialties.some(s => s.toLowerCase().includes(cuisine.toLowerCase()))
    )) return false;

    if (filters.capacity.length > 0 && !filters.capacity.some(capacity => {
      if (capacity.includes('+')) {
        return restaurant.cateringServices.minCapacity >= parseInt(capacity.replace('+', ''));
      }
      const [min, max] = capacity.split('-').map(Number);
      return restaurant.cateringServices.minCapacity >= min && restaurant.cateringServices.maxCapacity <= max;
    })) return false;

    if (filters.priceRange) {
      const price = restaurant.cateringServices.pricePerPerson || 0;
      if (price < filters.priceRange.min || price > filters.priceRange.max) return false;
    }

    if (filters.eventType.length > 0 && !filters.eventType.some(eventType =>
      restaurant.cateringServices.eventTypes.some(event => event.toLowerCase().includes(eventType.toLowerCase()))
    )) return false;

    if (filters.rating.length > 0 && !filters.rating.some(rating =>
      (restaurant.rating || 0) >= parseFloat(rating)
    )) return false;

    return true;
  });

  const sortedRestaurants = [...filteredRestaurants].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return (a.cateringServices?.pricePerPerson || 0) - (b.cateringServices?.pricePerPerson || 0);
      case 'price-high':
        return (b.cateringServices?.pricePerPerson || 0) - (a.cateringServices?.pricePerPerson || 0);
      case 'rating':
        return (b.rating || 0) - (a.rating || 0);
      case 'capacity':
        return (b.cateringServices?.maxCapacity || 0) - (a.cateringServices?.maxCapacity || 0);
      case 'popularity':
      default: {
        const ratingDiff = (b.rating || 0) - (a.rating || 0);
        if (ratingDiff !== 0) return ratingDiff;
        return (b.cateringServices?.services?.length || 0) - (a.cateringServices?.services?.length || 0);
      }
    }
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentRestaurants = sortedRestaurants.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedRestaurants.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    document.getElementById('cuisiniers')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sortBy]);

  const FILTER_GROUPS = [
    { key: 'cuisineType', label: t('catering.listing.filters.cuisineType') },
    { key: 'capacity', label: t('catering.listing.filters.capacity') },
    { key: 'eventType', label: t('catering.listing.filters.eventType') },
    { key: 'rating', label: t('catering.listing.filters.rating') },
  ];

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0', fontFamily: 'Archivo, sans-serif', fontSize: '14.5px', color: 'rgba(5,16,4,0.6)' }}>
        {error}
      </div>
    );
  }

  return (
    <div>
      {/* Recherche */}
      <div style={{ position: 'relative', maxWidth: '520px', marginBottom: '20px' }}>
        <Search size={18} style={{ position: 'absolute', left: '20px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(5,16,4,0.4)', pointerEvents: 'none' }} />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
          placeholder={t('catering.listing.searchPlaceholder')}
          style={{ ...inputStyle, width: '100%', padding: '13px 44px' }}
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => { setSearchQuery(''); setCurrentPage(1); }}
            aria-label={t('catering.listing.clear')}
            style={{ position: 'absolute', right: '18px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', cursor: 'pointer', fontSize: '18px', lineHeight: 1, color: 'rgba(5,16,4,0.4)' }}
          >
            ×
          </button>
        )}
      </div>

      <div className="flex flex-col lg:flex-row" style={{ gap: 'clamp(20px, 3vw, 32px)', alignItems: 'flex-start' }}>
        {/* Sidebar filtres */}
        <aside
          className="w-full lg:w-auto"
          style={{ flex: '0 0 auto', width: '100%', maxWidth: '280px', background: '#FCFBF5', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '28px', padding: '22px', display: 'flex', flexDirection: 'column', gap: '18px' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Filter size={17} style={{ color: PRIMARY }} />
            <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '17px' }}>{t('catering.listing.filtersTitle')}</strong>
            <button
              type="button"
              onClick={clearAllFilters}
              style={{ marginLeft: 'auto', border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, color: TERRA, textDecoration: 'underline' }}
            >
              {t('catering.listing.clear')}
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
            <span style={filterLabelStyle}>{t('catering.listing.city')}</span>
            <select
              value={filters.city}
              onChange={(e) => handleCityChange(e.target.value)}
              style={{ ...inputStyle, borderRadius: '14px', padding: '11px 16px', fontSize: '14px', width: '100%' }}
            >
              <option value="">{t('catering.listing.allCities')}</option>
              {availableCities.map((city) => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>

          {FILTER_GROUPS.map(({ key, label }) => (
            <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: '9px', borderTop: '1px solid rgba(5,16,4,0.08)', paddingTop: '16px' }}>
              <button
                type="button"
                onClick={() => toggleSection(key)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: 'none', background: 'none', cursor: 'pointer', padding: 0, fontFamily: 'Archivo, sans-serif', fontSize: '14px', fontWeight: 600, color: '#051004' }}
              >
                {label}
                {expandedSections[key] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {expandedSections[key] && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '2px' }}>
                  {filterOptions[key] && filterOptions[key].length > 0 ? filterOptions[key].map((option) => (
                    <label key={option.value} style={{ display: 'flex', alignItems: 'center', gap: '9px', cursor: 'pointer', fontSize: '14px', color: 'rgba(5,16,4,0.78)' }}>
                      <input
                        type="checkbox"
                        checked={filters[key].includes(option.value)}
                        onChange={() => handleFilterChange(key, option.value)}
                        style={{ width: '16px', height: '16px', accentColor: PRIMARY }}
                      />
                      {option.label}
                    </label>
                  )) : (
                    <span style={{ fontSize: '13px', color: 'rgba(5,16,4,0.4)', fontStyle: 'italic' }}>{t('catering.listing.noOptionAvailable')}</span>
                  )}
                </div>
              )}
            </div>
          ))}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '9px', borderTop: '1px solid rgba(5,16,4,0.08)', paddingTop: '16px' }}>
            <button
              type="button"
              onClick={() => toggleSection('price')}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: 'none', background: 'none', cursor: 'pointer', padding: 0, fontFamily: 'Archivo, sans-serif', fontSize: '14px', fontWeight: 600, color: '#051004' }}
            >
              {t('catering.listing.pricePerPerson')}
              {expandedSections.price ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {expandedSections.price && (
              <div style={{ paddingTop: '4px' }}>
                <PriceRangeSlider
                  min={filterOptions.priceMin || 0}
                  max={filterOptions.priceMax || 200}
                  step={5}
                  value={filters.priceRange || { min: 0, max: 200 }}
                  onChange={(range) => handleFilterChange('priceRange', range)}
                  formatLabel={(val) => t('catering.listing.pricePerPersonValue', { price: val })}
                />
              </div>
            )}
          </div>
        </aside>

        {/* Contenu */}
        <div style={{ flex: '1 1 0%', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div className="flex flex-wrap" style={{ alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
            <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '14px', color: 'rgba(5,16,4,0.66)' }}>
              {loading ? t('catering.listing.loading') : t('catering.listing.caterersFound', { count: sortedRestaurants.length })}
            </span>
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid rgba(5,16,4,0.14)', background: '#FCFBF5', borderRadius: '999px', padding: '10px 18px', cursor: 'pointer', fontFamily: 'Archivo, sans-serif', fontSize: '13.5px', fontWeight: 600, color: '#051004' }}
              >
                <ArrowUpDown size={14} />
                {sortOptions.find((opt) => opt.value === sortBy)?.label}
                <ChevronDown size={14} />
              </button>
              {showSortDropdown && (
                <div style={{ position: 'absolute', right: 0, marginTop: '8px', width: '200px', background: '#FCFBF5', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '18px', boxShadow: '0 14px 32px rgba(5,16,4,0.12)', overflow: 'hidden', zIndex: 10 }}>
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => { setSortBy(option.value); setShowSortDropdown(false); }}
                      style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer', padding: '11px 16px', fontFamily: 'Archivo, sans-serif', fontSize: '13.5px', background: sortBy === option.value ? '#FBF9D9' : 'transparent', color: sortBy === option.value ? PRIMARY : '#051004', fontWeight: sortBy === option.value ? 600 : 400 }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {loading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 290px), 1fr))', gap: 'clamp(16px, 2vw, 24px)' }}>
              {[0, 1, 2].map((i) => (
                <div key={i} className="animate-pulse" style={{ background: '#FCFBF5', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '22px', overflow: 'hidden' }}>
                  <div style={{ width: '100%', aspectRatio: '16 / 9', background: '#FBF9D9' }} />
                  <div style={{ padding: '34px 22px 22px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ height: '18px', width: '60%', background: '#FBF9D9', borderRadius: '999px' }} />
                    <div style={{ height: '12px', width: '75%', background: '#FBF9D9', borderRadius: '999px' }} />
                    <div style={{ height: '12px', width: '50%', background: '#FBF9D9', borderRadius: '999px' }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 290px), 1fr))', gap: 'clamp(16px, 2vw, 24px)' }}>
                {currentRestaurants.map((restaurant) => (
                  <Link
                    key={restaurant.id}
                    to={`/catering-service-details/${restaurant.id}`}
                    style={{ background: '#FCFBF5', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '22px', overflow: 'hidden', display: 'flex', flexDirection: 'column', color: '#051004', transition: 'box-shadow 160ms ease' }}
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 10px 22px rgba(5,16,4,0.08)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <span style={{ position: 'relative', display: 'block' }}>
                      <span style={{ display: 'block', width: '100%', aspectRatio: '16 / 9', background: '#FBF9D9', overflow: 'hidden' }}>
                        <SkeletonImage
                          src={restaurant.profile?.banner_image_url || restaurant.profile?.logo_url || namkeFallback}
                          alt={restaurant.name}
                          width="w-full"
                          height="h-full"
                          className="rounded-none"
                          fallbackSrc={namkeFallback}
                        />
                      </span>
                      <span style={{ position: 'absolute', bottom: '-26px', left: '20px', width: '62px', height: '62px', borderRadius: '999px', border: '4px solid #FCFBF5', background: '#FBF9D9', overflow: 'hidden', display: 'block' }}>
                        <SkeletonImage
                          src={restaurant.profile?.logo_url || namkeFallback}
                          alt=""
                          width="w-full"
                          height="h-full"
                          className="rounded-none"
                          fallbackSrc={namkeFallback}
                        />
                      </span>
                    </span>

                    <span style={{ padding: '34px 22px 22px', display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                      <span style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                        <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '20px' }}>{restaurant.name}</strong>
                        {restaurant.rating > 0 && (
                          <span style={{ marginLeft: 'auto', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '5px', fontFamily: 'Archivo, sans-serif', fontSize: '13.5px', fontWeight: 600, color: PRIMARY, whiteSpace: 'nowrap' }}>
                            <Star size={12} style={{ color: '#FFC717', fill: '#FFC717' }} />
                            {Number(restaurant.rating).toFixed(1)}
                          </span>
                        )}
                      </span>

                      <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13.5px', color: 'rgba(5,16,4,0.66)' }}>
                        {[restaurant.cuisine_type, restaurant.city].filter(Boolean).join(' · ')}
                      </span>

                      {restaurant.description && (
                        <span style={{ fontSize: '14.5px', lineHeight: 1.55, color: 'rgba(5,16,4,0.78)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {restaurant.description}
                        </span>
                      )}

                      <span className="flex flex-wrap" style={{ gap: '8px', marginTop: 'auto', paddingTop: '12px' }}>
                        <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', background: '#FBF9D9', padding: '7px 14px', borderRadius: '999px' }}>
                          {t('catering.listing.capacityBadge', { min: restaurant.cateringServices.minCapacity, max: restaurant.cateringServices.maxCapacity })}
                        </span>
                        {restaurant.cateringServices.pricePerPerson > 0 && (
                          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', background: '#FBF9D9', padding: '7px 14px', borderRadius: '999px' }}>
                            {t('catering.listing.fromPricePerPerson', { price: restaurant.cateringServices.pricePerPerson })}
                          </span>
                        )}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>

              {sortedRestaurants.length === 0 && (
                <div style={{ textAlign: 'center', padding: '56px 0' }}>
                  <Users size={40} style={{ margin: '0 auto 14px', color: 'rgba(5,16,4,0.2)' }} />
                  <strong style={{ display: 'block', fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '19px', marginBottom: '6px' }}>{t('catering.listing.noRestaurantFound')}</strong>
                  <span style={{ fontSize: '14px', color: 'rgba(5,16,4,0.55)' }}>{t('catering.listing.noRestaurantMatches')}</span>
                </div>
              )}

              {filteredRestaurants.length > itemsPerPage && (
                <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#FCFBF5', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '999px', padding: '6px' }}>
                    <button
                      type="button"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      style={{ border: 'none', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', background: 'transparent', borderRadius: '999px', padding: '9px 16px', fontFamily: 'Archivo, sans-serif', fontSize: '13.5px', fontWeight: 600, color: currentPage === 1 ? 'rgba(5,16,4,0.3)' : '#051004' }}
                    >
                      {t('catering.listing.previous')}
                    </button>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {Array.from({ length: totalPages }, (_, index) => {
                        const pageNumber = index + 1;
                        const isCurrentPage = pageNumber === currentPage;
                        const isNearCurrent = Math.abs(pageNumber - currentPage) <= 2;

                        if (isNearCurrent || pageNumber === 1 || pageNumber === totalPages) {
                          return (
                            <button
                              key={pageNumber}
                              type="button"
                              onClick={() => handlePageChange(pageNumber)}
                              style={{ border: 'none', cursor: 'pointer', borderRadius: '999px', width: '34px', height: '34px', fontFamily: 'Archivo, sans-serif', fontSize: '13.5px', fontWeight: 600, background: isCurrentPage ? PRIMARY : 'transparent', color: isCurrentPage ? '#FBF9D9' : 'rgba(5,16,4,0.7)' }}
                            >
                              {pageNumber}
                            </button>
                          );
                        } else if (pageNumber === 2 && currentPage > 4) {
                          return <span key={pageNumber} style={{ padding: '0 4px', color: 'rgba(5,16,4,0.35)' }}>…</span>;
                        } else if (pageNumber === totalPages - 1 && currentPage < totalPages - 3) {
                          return <span key={pageNumber} style={{ padding: '0 4px', color: 'rgba(5,16,4,0.35)' }}>…</span>;
                        }
                        return null;
                      })}
                    </div>

                    <button
                      type="button"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      style={{ border: 'none', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', background: 'transparent', borderRadius: '999px', padding: '9px 16px', fontFamily: 'Archivo, sans-serif', fontSize: '13.5px', fontWeight: 600, color: currentPage === totalPages ? 'rgba(5,16,4,0.3)' : '#051004' }}
                    >
                      {t('catering.listing.next')}
                    </button>
                  </div>

                  <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13px', color: 'rgba(5,16,4,0.55)' }}>
                    {t('catering.listing.showingRange', { from: indexOfFirstItem + 1, to: Math.min(indexOfLastItem, sortedRestaurants.length), total: sortedRestaurants.length })}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CateringListing;
